import { useSyncExternalStore } from "react";
import { api, apiFetch } from "@/lib/api";
import { readSSE } from "@/lib/sse";

// Live answer streams, kept outside React so they survive navigation between pages.
const entries = new Map();
const aliases = new Map();
const listeners = new Set();
let queryClient = null;
let frame = null;

export function bindQueryClient(client) {
  queryClient = client;
}

const resolve = (key) => aliases.get(key) || key;
const notify = () => listeners.forEach((l) => l());

function scheduleNotify() {
  if (frame) return;
  frame = requestAnimationFrame(() => {
    frame = null;
    notify();
  });
}

function update(key, patch, { batched = false } = {}) {
  const k = resolve(key);
  const prev = entries.get(k);
  if (!prev) return;
  entries.set(k, { ...prev, ...(typeof patch === "function" ? patch(prev) : patch) });
  batched ? scheduleNotify() : notify();
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useStreamEntry(key) {
  return useSyncExternalStore(subscribe, () => (key ? entries.get(resolve(key)) : undefined));
}

export function getStreamEntry(key) {
  return key ? entries.get(resolve(key)) : undefined;
}

export function clearStream(key) {
  entries.delete(resolve(key));
  notify();
}

function lastIndex(list, predicate) {
  for (let i = list.length - 1; i >= 0; i -= 1) if (predicate(list[i])) return i;
  return -1;
}

export function mergeMessages(base, entry) {
  if (!entry) return base;
  let msgs = base.filter((m) => m.id !== entry.assistantMessageId && m.id !== entry.userMessageId);
  if (entry.kind === "retry") msgs = msgs.slice(0, lastIndex(msgs, (m) => m.role === "user") + 1);
  if (entry.kind === "message") {
    msgs = [...msgs, { id: entry.userMessageId || "pending-user", role: "user", content: entry.userContent, attachments: entry.attachments, status: entry.status === "error" ? "unsent" : "complete" }];
  }
  if (entry.status !== "error") {
    const live = entry.status === "connecting" || entry.status === "streaming";
    msgs = [...msgs, { id: entry.assistantMessageId || "pending-assistant", role: "assistant", content: entry.text, status: live ? "streaming" : entry.status, live: true }];
  }
  return msgs;
}

function commitToCache(entry, status) {
  if (!queryClient || !entry.conversationId) return;
  const finished = { ...entry, status };
  const attached = (entry.attachments || []).map((a) => ({ ...a, status: "completed", message_id: entry.userMessageId }));
  queryClient.setQueryData(["conversation", entry.conversationId], (old) => ({
    ...old,
    conversation: old?.conversation ?? { id: entry.conversationId, title: entry.title || "New chat" },
    messages: mergeMessages(old?.messages ?? [], finished).map(({ attachments, ...m }) => ({ ...m, live: false })),
    files: [...(old?.files ?? []).filter((f) => !attached.some((a) => a.id === f.id)), ...attached],
  }));
  ["me", "conversations", "usage", "project", "projects"].forEach((k) => queryClient.invalidateQueries({ queryKey: [k] }));
}

function applyTitle(conversationId, title) {
  if (!queryClient) return;
  queryClient.setQueryData(["conversation", conversationId], (old) =>
    old ? { ...old, conversation: { ...old.conversation, title } } : old,
  );
  queryClient.invalidateQueries({ queryKey: ["conversations"] });
}

async function requestStop(requestId) {
  try {
    await api.post(`/chat/requests/${requestId}/stop`);
  } catch {
    // The answer may have already finished; nothing else to do.
  }
}

export function stopStream(key) {
  const entry = getStreamEntry(key);
  if (!entry) return;
  if (entry.requestId) requestStop(entry.requestId);
  else update(key, { stopRequested: true });
}

async function run(key) {
  const entry = entries.get(key);
  const path = entry.kind === "retry" ? `/chat/conversations/${entry.conversationId}/retry` : "/chat/messages";
  const fileIds = (entry.attachments || []).map((a) => a.id);
  const body = entry.kind === "retry" ? undefined : { content: entry.userContent, ...(entry.conversationId ? { conversation_id: entry.conversationId } : {}), ...(fileIds.length ? { file_ids: fileIds } : {}), ...(entry.projectId && !entry.conversationId ? { project_id: entry.projectId } : {}), ...(entry.mode === "code" ? { mode: "code" } : {}) };
  let response;
  try {
    response = await apiFetch(path, { method: "POST", body, raw: true });
  } catch (e) {
    if (e?.name !== "AbortError") console.warn("[ChatSleek] send failed", { code: e.code, status: e.status, requestId: e.requestId, path });
    update(key, { status: "error", error: { code: e.code || "error", message: e.message, requestId: e.requestId } });
    return;
  }
  let done = false;
  try {
    for await (const { event, data } of readSSE(response)) {
      if (event === "meta") {
        const current = getStreamEntry(key);
        if (key !== data.conversation_id) {
          entries.set(data.conversation_id, { ...current, key: data.conversation_id });
          entries.delete(key);
          aliases.set(key, data.conversation_id);
        }
        update(key, { status: "streaming", requestId: data.request_id, conversationId: data.conversation_id, userMessageId: data.user_message_id, assistantMessageId: data.assistant_message_id });
        if (current?.stopRequested) requestStop(data.request_id);
      } else if (event === "delta") {
        update(key, (prev) => ({ text: prev.text + data.text }), { batched: true });
      } else if (event === "error") {
        update(key, { error: { code: data.code, message: data.message, requestId: data.request_id } });
      } else if (event === "done") {
        done = true;
        const current = getStreamEntry(key);
        commitToCache(current, data.status);
        clearStream(key);
      } else if (event === "title") {
        applyTitle(data.conversation_id, data.title);
      } else if (event === "task_suggestion") {
        queryClient?.setQueryData(["conversation", data.conversation_id], (old) => (old ? { ...old, task_suggestion: data } : old));
      }
    }
  } catch {
    // Stream dropped (network); the server keeps going and saves the answer.
  }
  if (!done && getStreamEntry(key)) {
    update(key, { status: "disconnected" });
    const conversationId = getStreamEntry(key)?.conversationId;
    setTimeout(() => {
      clearStream(key);
      if (conversationId) queryClient?.invalidateQueries({ queryKey: ["conversation", conversationId] });
    }, 2500);
  }
}

export function startStream({ conversationId = null, content = null, kind = "message", key, attachments = [], projectId = null, mode = "chat" }) {
  const streamKey = key || conversationId || `pending-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  aliases.delete(streamKey);
  entries.set(streamKey, {
    key: streamKey, kind, mode, conversationId, projectId, userContent: content, attachments, text: "", status: "connecting",
    requestId: null, userMessageId: null, assistantMessageId: null, error: null, stopRequested: false,
  });
  notify();
  run(streamKey);
  return streamKey;
}
